import * as React from 'react';

import { PaywallTouchEventAdapter } from '~analytics/adapters/PaywallTouchEventAdapter';
import { PaywallScreens } from '~analytics/types';
import { translate } from '~l10n/client';
import { UIButton } from '~shared/components/buttons';
import { Dialog, DialogProps, DialogTitle } from '~shared/components/Dialog';
import { Inline } from '~shared/components/Inline';
import { Stack } from '~shared/components/Stack';
import {
  DialogProps as AsyncDialogProps,
  useDialog,
  useOpenDialogAnimation,
} from '~shared/context/AsyncDialogContext';

import { PaywallUpgradeButton } from './PaywallUpgradeButton';

export interface Props extends Pick<DialogProps, 'open' | 'onClose'> {
  paywallScreen: PaywallScreens;
}

export function PaywallDialogAiInsights({
  open,
  onClose,
  paywallScreen,
}: Props) {
  React.useEffect(() => {
    if (open) {
      new PaywallTouchEventAdapter(paywallScreen).sendAnalytics();
    }
  }, [open, paywallScreen]);

  return (
    <Dialog
      id="paywall-ai-insights"
      title={translate("You've Reached Your Plan's Limit")}
      padding="confirmation"
      open={open}
      onClose={onClose}
      closeLabel={translate('Close')}
    >
      {(headerProps) => (
        <Stack align="center" space="8">
          <h3 className="m-0">
            <DialogTitle ref={headerProps.headingRef}>
              {headerProps.title}
            </DialogTitle>
          </h3>
          <div className="font-sans text-sm leading-8 text-blue-900">
            {translate('Upgrade your plan for extended analysis access.')}
          </div>
          <Inline align="center">
            <UIButton key="cancel" onClick={onClose}>
              {translate('Cancel')}
            </UIButton>
            <PaywallUpgradeButton
              key="upgrade"
              appearance="primary"
              paywallScreen={paywallScreen}
            />
          </Inline>
        </Stack>
      )}
    </Dialog>
  );
}

function AsyncPaywallDialogAiInsights({ onResolve }: AsyncDialogProps) {
  const isOpen = useOpenDialogAnimation();
  return (
    <PaywallDialogAiInsights
      open={isOpen}
      onClose={() => onResolve({ success: false })}
      paywallScreen={PaywallScreens.AiInsightsLimit}
    />
  );
}

export function useOpenPaywallDialogAiInsights() {
  const { openDialog } = useDialog();
  return () => openDialog(AsyncPaywallDialogAiInsights);
}
